import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import type { MorningDashboardContextValue, Props } from './MorningDashboardContextProps';
export type { MorningDashboardContextValue, Props } from './MorningDashboardContextProps';
import { sortWidgetByOrder } from '@morning-dashboard/helpers';
import { useWidgetOrder, useSaveWidgetOrder } from '../hooks';
import { DASHBOARD_ACTIONS_TYPE, dashboardReducer, initialDashboardState } from './MorningDashboardReducer';

const MorningDashboardContext = createContext<MorningDashboardContextValue | undefined>(undefined);

// Provider
export const MorningDashboardProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialDashboardState);
  const { isCustomizeOpened, order, refreshKey } = state;

  const { data: fetchedOrder } = useWidgetOrder();
  const { mutate: saveLayout } = useSaveWidgetOrder();

  useEffect(() => {
    if (!fetchedOrder) return;
    dispatch({ type: DASHBOARD_ACTIONS_TYPE.load_order, payload: sortWidgetByOrder(fetchedOrder) });
  }, [fetchedOrder]);

  const setIsCustomizeOpened = useCallback<React.Dispatch<React.SetStateAction<boolean>>>((value) => {
    const next = typeof value === 'function' ? value(state.isCustomizeOpened) : value;
    dispatch({ type: DASHBOARD_ACTIONS_TYPE.set_customize_opened, payload: next });
  }, [state.isCustomizeOpened]);

  const swap = useCallback((fromIndex: number, toIndex: number) => {
    dispatch({ type: DASHBOARD_ACTIONS_TYPE.swap, fromIndex, toIndex });
  }, []);

  const confirmOrder = useCallback(() => {
    saveLayout(order, {
      onSuccess: () => dispatch({ type: DASHBOARD_ACTIONS_TYPE.confirm_order }),
    });
  }, [order, saveLayout]);

  const revertOrder = useCallback(() => {
    dispatch({ type: DASHBOARD_ACTIONS_TYPE.revert_order });
  }, []);

  const triggerRefresh = useCallback(() => {
    dispatch({ type: DASHBOARD_ACTIONS_TYPE.trigger_refresh });
  }, []);

  const value = useMemo<MorningDashboardContextValue>(() => {
    return {
      isCustomizeOpened,
      setIsCustomizeOpened,
      order,
      swap,
      confirmOrder,
      revertOrder,
      refreshKey,
      triggerRefresh,
    };
  }, [isCustomizeOpened, order, swap, confirmOrder, revertOrder, refreshKey, triggerRefresh]);

  return <MorningDashboardContext.Provider value={value}>{children}</MorningDashboardContext.Provider>;
};

// Hook
export const useMorningDashboard = () => {
  const ctx = useContext(MorningDashboardContext);
  if (!ctx) {
    throw new Error('useMorningDashboard must be used within a MorningDashboardProvider');
  }
  return ctx;
};
